import { LanguageSwitcher } from '@ecom/ui-kit';
import { motion } from 'framer-motion';
import { type ReactElement, type ReactNode } from 'react';

import { useLocale } from '@/lib/locale';

/**
 * The frame of every signed-out ADMIN screen (Login, password change), plus a
 * `LanguageSwitcher` — nothing pre-auth can rely on the Topbar for a language
 * choice, so this shared layout carries its own.
 *
 * Visual identity: ABOUEB (أبواب — E-commerce Solutions) — blue + navy. The
 * brand blue is scoped to `.brand-auth` (styles/globals.css) so it recolours
 * these pre-auth screens only, never the Souk-Ember tokens the authenticated
 * app is built on. The admin side wears the deeper-navy "authority" treatment
 * that tells it apart from the merchant space while keeping one identity.
 */

const LOGO_SRC = `${import.meta.env.BASE_URL}aboueb-logo.jpg`;

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}): ReactElement {
  const { locale, setLocale } = useLocale();

  return (
    <div className="brand-auth grid min-h-dvh bg-ground lg:grid-cols-[1fr_1.05fr]">
      {/* ---------------------------------------------------------- brand panel */}
      <aside className="relative hidden overflow-hidden bg-gradient-to-br from-[#0e2a63] via-[#163a86] to-[#1f4fa8] p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-12">
        {/* luminous blue glows for depth */}
        <div className="pointer-events-none absolute -left-24 top-6 h-72 w-72 rounded-full bg-[#46b3e6]/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-[#2e7dc1]/30 blur-3xl" />
        {/* faint large ring echoing the logo swirl */}
        <div className="pointer-events-none absolute -right-28 top-1/4 h-96 w-96 rounded-full border-[14px] border-white/5" />

        <span className="relative z-10 flex h-[4.5rem] w-fit items-center rounded-2xl bg-white px-4 shadow-lg">
          <img src={LOGO_SRC} alt="ABOUEB" className="h-14 w-auto object-contain" />
        </span>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative z-10 max-w-md"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-[#8fd4ec]/40 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#9bd9f0]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#46b3e6]" />
            Console d&apos;administration
          </span>
          <h2 className="mt-5 font-display text-3xl font-bold leading-tight xl:text-4xl">
            Pilotez votre plateforme e-commerce
            <span className="block text-[#7fd0f0]">en toute simplicité.</span>
          </h2>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/70">
            Commerçants, catalogues, commandes et livraisons — un espace unique, sécurisé et en
            temps réel.
          </p>
        </motion.div>

        <p className="relative z-10 text-xs text-white/50">
          © {new Date().getFullYear()} ABOUEB — E-commerce Solutions
        </p>
      </aside>

      {/* ------------------------------------------------------------- form side */}
      <main className="flex min-h-dvh flex-col px-5 py-6 sm:px-10">
        <div className="flex justify-end">
          <LanguageSwitcher locale={locale} onChange={setLocale} />
        </div>

        <div className="flex flex-1 items-center justify-center py-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="w-full max-w-md"
          >
            {/* brand header — shown when the side panel is hidden (tablet/mobile) */}
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <span className="flex h-14 items-center rounded-xl border border-line bg-surface px-3 shadow-card">
                <img src={LOGO_SRC} alt="ABOUEB" className="h-10 w-auto object-contain" />
              </span>
              <span className="text-sm font-semibold text-inkSoft">
                Console d&apos;administration
              </span>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-7 shadow-card sm:p-8">
              <div className="mb-6 h-1 w-10 rounded-full bg-accent" aria-hidden="true" />
              <h1 className="font-display text-2xl font-bold text-ink">{title}</h1>
              <p className="mt-1.5 text-sm text-inkFaint">{subtitle}</p>
              <div className="mt-6">{children}</div>
            </div>

            {footer !== undefined ? (
              <div className="mt-5 text-center text-sm text-inkFaint">{footer}</div>
            ) : null}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
